import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ORDER_CHANNELS, can, findShortages } from '@pl/domain';
import type { OrderChannel, OrderDetailDto, ShortageDto, SiteDto, SkuDto, StockRowDto } from '@pl/domain';
import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError } from '../../lib/api';
import { api } from '../../lib/api';
import { CHANNEL_DOT, channelName, skuName } from '../../lib/format';
import { useAvailability, useMe, useSkus } from '../../lib/queries';
import { uuid } from '../../lib/uuid';
import { Empty, ErrorNote, RefetchNote, SiteTag, SkuTag, Spinner } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { LiveDot } from '../../ui/LiveDot';
import { SkuPicker } from './SkuPicker';
import s from './NewOrder.module.css';

interface DraftLine {
  id: string;
  skuId: string | null;
  qty: number;
}

interface InsufficientStockDetails {
  shortages: ShortageDto[];
  elsewhere: StockRowDto[];
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * A native `<select>` with a real `<label>`, so the field is keyboard-operable and announced by
 * name. The mockup's look (channel dot on the left, chevron on the right) is CSS only: both
 * decorations are `pointer-events: none` so the whole control stays clickable.
 */
function ChannelField({ value, onChange }: { value: OrderChannel; onChange: (c: OrderChannel) => void }) {
  const selectId = `${useId()}-channel`;
  return (
    <div className="fld">
      <label htmlFor={selectId}>Channel</label>
      <div className={`inp ${s.chanField}`}>
        {/* The mockup's chrome, drawn for the eye only; the transparent native select on top of
            it owns every interaction, so neither piece can intercept a click from the other. */}
        <span className="channel" aria-hidden="true">
          <i style={{ background: CHANNEL_DOT[value] }} />
          {channelName(value)}
        </span>
        <Icon name="chev-down" className="chev" />
        <select id={selectId} className={s.chanSelect} value={value} onChange={(e) => onChange(e.target.value as OrderChannel)}>
          {ORDER_CHANNELS.map((c) => (
            <option key={c} value={c}>
              {channelName(c)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export function NewOrder() {
  const meQuery = useMe();
  const skusQuery = useSkus();
  const nav = useNavigate();
  const qc = useQueryClient();

  const [channel, setChannel] = useState<OrderChannel>('sales');
  const [externalRef, setExternalRef] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [siteId, setSiteId] = useState<string | undefined>(undefined);
  const [requiredBy, setRequiredBy] = useState('');
  const [notes, setNotes] = useState('');
  const [allowBackorder, setAllowBackorder] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>([{ id: uuid(), skuId: null, qty: 0 }]);
  const [submitError, setSubmitError] = useState<ApiError | null>(null);

  const sites = meQuery.data?.sites ?? [];
  const skus = skusQuery.data ?? [];
  const skuMap = useMemo(() => new Map(skus.map((sku) => [sku.id, sku])), [skus]);
  const siteMap = useMemo(() => new Map(sites.map((site) => [site.id, site])), [sites]);

  // Default the ship-from site to the first live one, once sites have loaded.
  useEffect(() => {
    if (siteId || sites.length === 0) return;
    const firstLive = sites.find((site) => site.live);
    if (firstLive) setSiteId(firstLive.id);
  }, [sites, siteId]);

  // A shortage callout is only valid for the site it was raised against — once the
  // ship-from site changes (including via the callout's own "Ship from X" button),
  // drop the stale server error and fall back to the live client-side preview.
  useEffect(() => {
    setSubmitError(null);
  }, [siteId]);

  const chosenSkuIds = useMemo(() => {
    const seen = new Set<string>();
    for (const line of lines) if (line.skuId) seen.add(line.skuId);
    return [...seen];
  }, [lines]);
  const chosenSkuOrder = useMemo(() => {
    const seen = new Set<string>();
    const order: string[] = [];
    for (const line of lines) {
      if (line.skuId && !seen.has(line.skuId)) {
        seen.add(line.skuId);
        order.push(line.skuId);
      }
    }
    return order;
  }, [lines]);
  const needBySku = useMemo(() => {
    const m = new Map<string, number>();
    for (const line of lines) if (line.skuId) m.set(line.skuId, (m.get(line.skuId) ?? 0) + (line.qty || 0));
    return m;
  }, [lines]);

  const availabilityQuery = useAvailability(chosenSkuIds);
  /**
   * Two different zeros have to stay apart on this screen. `GET /availability` omits sites that
   * hold nothing and have nothing reserved, so a row missing from a *successful* response really
   * is 0. But a response that has not arrived — still loading, key just changed, or the request
   * failed — is not 0, it is unknown, and this page's whole job is live stock. `null` means
   * unknown and every caller renders it as "—" rather than inventing a number.
   */
  const availabilityKnown = availabilityQuery.isSuccess;
  const availRow = (skuId: string, forSiteId: string | undefined): StockRowDto | null => {
    if (!forSiteId || !availabilityKnown) return null;
    return availabilityQuery.data.find((r) => r.siteId === forSiteId && r.skuId === skuId) ?? { siteId: forSiteId, skuId, inStock: 0, atDock: 0, reserved: 0, available: 0 };
  };

  const localShortages = useMemo<ShortageDto[]>(() => {
    if (!siteId || !availabilityKnown) return [];
    const availableBySku: Record<string, number> = {};
    for (const skuId of chosenSkuIds) {
      const row = availRow(skuId, siteId);
      if (!row) return []; // one unknown SKU is enough to make the whole preview a guess
      availableBySku[skuId] = row.available;
    }
    const requested = lines.filter((l) => l.skuId && l.qty > 0).map((l) => ({ skuId: l.skuId!, qty: l.qty }));
    return findShortages(requested, availableBySku);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, siteId, availabilityQuery.data, availabilityKnown, chosenSkuIds]);

  const serverDetails = submitError?.code === 'INSUFFICIENT_STOCK' ? (submitError.details as InsufficientStockDetails) : null;
  const shortages = serverDetails?.shortages ?? localShortages;
  const elsewhereRows = serverDetails?.elsewhere ?? availabilityQuery.data ?? [];

  function eligibleElsewhere(shortage: ShortageDto) {
    return elsewhereRows.filter((r) => r.skuId === shortage.skuId && r.siteId !== siteId && (siteMap.get(r.siteId)?.live ?? false) && r.available >= shortage.requested);
  }

  const firstShortageLineNo = useMemo(() => {
    if (!serverDetails) return null;
    const skuIds = new Set(serverDetails.shortages.map((sh) => sh.skuId));
    const idx = lines.findIndex((l) => l.skuId && skuIds.has(l.skuId));
    return idx >= 0 ? pad(idx + 1) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverDetails, lines]);

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<OrderDetailDto>('/orders', body),
    onSuccess: (order) => {
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'orders' || q.queryKey[0] === 'stock' || q.queryKey[0] === 'availability' });
      nav(`/office/orders/${order.id}`);
    },
    onError: (e) => {
      if (e instanceof ApiError) setSubmitError(e);
    },
  });

  const trimmedLines = lines.filter((l): l is DraftLine & { skuId: string } => Boolean(l.skuId) && l.qty >= 1).map((l) => ({ skuId: l.skuId, qty: l.qty }));
  const role = meQuery.data?.role;
  const canOrders = Boolean(role && can(role, 'orders'));
  const canSave = canOrders && Boolean(siteId) && customerName.trim().length > 0 && trimmedLines.length > 0 && !saveMutation.isPending;

  function handleSave() {
    if (!canSave || !siteId) return;
    setSubmitError(null);
    saveMutation.mutate({
      siteId,
      channel,
      externalRef: externalRef.trim() || null,
      customerName: customerName.trim(),
      requiredBy: requiredBy || null,
      notes: notes.trim() || null,
      allowBackorder,
      lines: trimmedLines,
    });
  }

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!((e.metaKey || e.ctrlKey) && e.key === 'Enter')) return;
      // An open typeahead owns Enter: it is choosing a SKU, not saving the order. The picker
      // already stops the event, but staying independent of it means neither can drop a line.
      const target = e.target as HTMLElement | null;
      if (target?.getAttribute('role') === 'combobox' && target.getAttribute('aria-expanded') === 'true') return;
      e.preventDefault();
      handleSaveRef.current();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function updateLine(id: string, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }
  function removeLine(id: string) {
    setLines((prev) => (prev.length === 1 ? [{ id: uuid(), skuId: null, qty: 0 }] : prev.filter((l) => l.id !== id)));
  }
  function addLine() {
    setLines((prev) => [...prev, { id: uuid(), skuId: null, qty: 0 }]);
  }
  function selectSku(lineId: string, sku: SkuDto) {
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.id === lineId);
      const next = prev.map((l) => (l.id === lineId ? { ...l, skuId: sku.id } : l));
      if (idx === prev.length - 1) next.push({ id: uuid(), skuId: null, qty: 0 });
      return next;
    });
  }
  function otherLineTag(currentLineId: string, skuId: string): string | null {
    const idx = lines.findIndex((l) => l.id !== currentLineId && l.skuId === skuId);
    return idx >= 0 ? `line ${pad(idx + 1)}` : null;
  }

  function shipAllFrom(candidateSiteId: string): { ok: true; totalBoxes: number } | { ok: false; skuId: string; short: number } | null {
    let total = 0;
    for (const [skuId, need] of needBySku) {
      total += need;
      const row = availRow(skuId, candidateSiteId);
      if (!row) return null; // unknown, so this site cannot be compared at all
      if (row.available < need) return { ok: false, skuId, short: need - row.available };
    }
    return { ok: true, totalBoxes: total };
  }

  const shipSite = sites.find((site) => site.id === siteId);
  const shownShortageSkus = new Set<string>();
  const filledLineCount = lines.filter((l) => l.skuId).length;

  // Only a query with nothing to show replaces the form. A failed background refresh of one that has
  // data leaves the form — and whatever has been typed into it — where it is.
  const failedQueries = [meQuery, skusQuery].filter((q) => q.isLoadingError);

  let body: ReactNode;
  if (failedQueries.length > 0) {
    body = (
      <div className="panel">
        <div className="panel-b stack" style={{ gap: 14, alignItems: 'flex-start' }}>
          <ErrorNote error={failedQueries[0]!.error} />
          <button type="button" className="btn" onClick={() => failedQueries.forEach((q) => q.refetch())}>
            Try again
          </button>
        </div>
      </div>
    );
  } else if (!meQuery.data || !skusQuery.data) {
    body = (
      <div className="row">
        <Spinner label="Loading" />
      </div>
    );
  } else {
    body = (
      <div className={s.page}>
        <div className={s.left}>
          <RefetchNote queries={[meQuery, skusQuery]} />
          <div className="panel">
            <div className={s.fields}>
              <ChannelField value={channel} onChange={setChannel} />
              <label className="fld">
                <span>External order #</span>
                <input className="inp mono" value={externalRef} onChange={(e) => setExternalRef(e.target.value)} placeholder="PH-0821" />
              </label>
              <label className="fld">
                <span>Customer</span>
                <input className="inp" value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
              </label>
              <div className={`fld ${s.wide2}`}>
                <span>Ship from</span>
                <div className={`seg ${s.segFull}`}>
                  {sites.map((site) => (
                    <button
                      key={site.id}
                      type="button"
                      className={siteId === site.id ? 'on' : undefined}
                      aria-pressed={siteId === site.id}
                      disabled={!site.live}
                      onClick={() => setSiteId(site.id)}
                    >
                      <span className="code">{site.shortCode}</span>
                      {site.live ? site.name : 'Not live'}
                    </button>
                  ))}
                </div>
              </div>
              <label className="fld">
                <span>Required by</span>
                <input type="date" className="inp" value={requiredBy} onChange={(e) => setRequiredBy(e.target.value)} />
              </label>
              <label className={`fld ${s.wide3}`}>
                <span>Notes</span>
                <textarea className="inp" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </label>
            </div>
          </div>

          <div className={s.secRow}>
            <h2>Lines</h2>
            <span className="chip" style={{ padding: '1px 6px' }}>
              {lines.filter((l) => l.skuId).length}
            </span>
            <span className="grow" />
            <button type="button" className="btn sm ghost" onClick={addLine}>
              <Icon name="plus" />
              Add line
            </button>
            <span className="hint">
              Type a code, color or cable type · <span className="kbd inl">↵</span> adds a line
            </span>
          </div>

          <div className={`panel ${s.linesP}`}>
            <div className={s.lh}>
              <span>#</span>
              <span>SKU</span>
              <span className="r">Boxes</span>
              <span className="r">{shipSite ? `${shipSite.shortCode} avail.` : 'Avail.'}</span>
              <span className="r">After save</span>
              <span />
            </div>
            {lines.map((line, i) => {
              const sku = line.skuId ? skuMap.get(line.skuId) : undefined;
              const shipRow = line.skuId ? availRow(line.skuId, siteId) : null;
              const available = shipRow ? shipRow.available : null;
              const afterSave = available === null ? null : available - line.qty;
              const shortage = line.skuId ? shortages.find((sh) => sh.skuId === line.skuId) : undefined;
              const showCallout = Boolean(shortage && line.skuId && !shownShortageSkus.has(line.skuId));
              if (showCallout && line.skuId) shownShortageSkus.add(line.skuId);

              return (
                <Fragment key={line.id}>
                  <div className={`${s.lr}${shortage ? ` ${s.err}` : ''}`}>
                    <span className={s.no}>{pad(i + 1)}</span>
                    <SkuPicker skus={skus} sites={sites} value={sku ?? null} onSelect={(picked) => selectSku(line.id, picked)} lineTag={(skuId) => otherLineTag(line.id, skuId)} />
                    <input
                      type="number"
                      min={1}
                      step={1}
                      className={`inp ${s.qty}`}
                      aria-label="Boxes"
                      placeholder="0"
                      value={line.qty || ''}
                      onChange={(e) => updateLine(line.id, { qty: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
                    />
                    <span className={`${s.av}${available !== null && available < 0 ? ' neg' : ''}`}>
                      {available === null ? (
                        <span className="faint">—</span>
                      ) : (
                        <>
                          {available}
                          <small>
                            {shipRow?.inStock ?? 0} in · {shipRow?.reserved ?? 0} res.
                          </small>
                        </>
                      )}
                    </span>
                    <span className={`${s.av}${afterSave !== null && afterSave < 0 ? ' neg' : ''}`}>{afterSave === null ? <span className="faint">—</span> : afterSave}</span>
                    <button type="button" className={s.rm} aria-label="Remove line" onClick={() => removeLine(line.id)}>
                      <Icon name="x" />
                    </button>
                  </div>

                  {showCallout && shortage && !serverDetails && (
                    <div className={s.valWrap}>
                      <div className={s.val}>
                        <Icon name="alert" />
                        <div>
                          <p>
                            <b>
                              {shipSite?.name ?? '—'} has {shortage.available} available
                            </b>{' '}
                            ({shipRow?.inStock ?? 0} in stock, {shipRow?.reserved ?? 0} reserved).{' '}
                            {eligibleElsewhere(shortage)
                              .map((r) => `${siteMap.get(r.siteId)?.name ?? r.siteId} has ${r.available}`)
                              .join(', ')}
                            {eligibleElsewhere(shortage).length > 0 ? '.' : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {showCallout && shortage && serverDetails && (
                    <div className="callout bad" role="alert">
                      <span className="ic">
                        <Icon name="alert" />
                      </span>
                      <div>
                        <p>
                          {shipSite?.name ?? '—'} has {shortage.available} available of {skuName(sku)} (you asked {shortage.requested}).
                        </p>
                        <div className={s.valActs}>
                          {eligibleElsewhere(shortage).map((r) => {
                            const site = siteMap.get(r.siteId);
                            if (!site) return null;
                            return (
                              <button key={site.id} type="button" className="btn sm" onClick={() => setSiteId(site.id)}>
                                <Icon name="site" />
                                Ship from {site.name}
                              </button>
                            );
                          })}
                          <label className={s.checkLabel}>
                            <input type="checkbox" className={s.checkbox} checked={allowBackorder} onChange={(e) => setAllowBackorder(e.target.checked)} aria-label="Allow backorder (recorded)" />
                            <span className={s.checkBox} aria-hidden="true" />
                            Allow backorder <small>(recorded)</small>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>

        <aside className={`panel ${s.avp}`}>
          <div className="panel-h">
            <h2>Availability across sites</h2>
            <span className="grow" />
            <LiveDot label="live" />
          </div>
          {availabilityQuery.isError && (
            <div className={s.avErr}>
              <ErrorNote error={availabilityQuery.error} />
              <button type="button" className="btn sm" onClick={() => availabilityQuery.refetch()}>
                Try again
              </button>
            </div>
          )}
          {chosenSkuOrder.length === 0 ? (
            <Empty title="No lines yet" description="Add a SKU to a line to see availability across sites." />
          ) : (
            chosenSkuOrder.map((skuId) => {
              const sku = skuMap.get(skuId);
              if (!sku) return null;
              const need = needBySku.get(skuId) ?? 0;
              return (
                <div className={s.avSku} key={skuId}>
                  <div className={s.topL}>
                    <SkuTag sku={sku} withCode />
                    <span className="grow" />
                    <span className={s.need}>
                      needs <b>{need}</b>
                    </span>
                  </div>
                  <div className={s.cells}>
                    {sites.map((site) => {
                      const row = site.live ? availRow(skuId, site.id) : null;
                      const isFrom = site.id === siteId;
                      const isBad = isFrom && site.live && row !== null && row.available < need;
                      const cellClass = [s.cell, !site.live ? s.off : isBad ? s.bad : isFrom ? s.from : ''].filter(Boolean).join(' ');
                      return (
                        <div key={site.id} className={cellClass}>
                          <div className={s.cellH}>
                            <SiteTag site={site} />
                            {isFrom && site.live && <small>ship from</small>}
                            {!site.live && <small>not live</small>}
                          </div>
                          <span className={s.v}>{row ? row.available : '—'}</span>
                          <span className={s.d}>{!site.live ? 'no stock yet' : row ? `${row.inStock} in · ${row.reserved} res.` : availabilityQuery.isError ? 'not available' : 'checking…'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}

          {chosenSkuOrder.length > 0 && availabilityKnown && (
            <div className={s.cmp}>
              <span className="sec-k" style={{ marginBottom: 2 }}>
                If shipped from
              </span>
              {sites
                .filter((site) => site.live)
                .map((site) => {
                  const result = shipAllFrom(site.id);
                  if (!result) return null; // availability unknown for this site — say nothing
                  const isSel = site.id === siteId;
                  return (
                    <div key={site.id} className={`${s.cmpRow}${isSel ? ` ${s.sel}` : ''}`}>
                      <span className="site-tag" style={{ justifySelf: 'start' }}>
                        {site.shortCode}
                      </span>
                      <span className="row" style={{ gap: 6 }}>
                        {result.ok ? (
                          <>
                            <b>{filledLineCount === 1 ? 'This line is covered' : `All ${filledLineCount} lines covered`}</b> · {result.totalBoxes} boxes
                          </>
                        ) : (
                          <>
                            <b>Short {result.short}</b> on <SkuTag sku={skuMap.get(result.skuId)} short />
                          </>
                        )}
                      </span>
                      <span className={`${s.ic} ${result.ok ? s.ok : s.bad}`}>
                        <Icon name={result.ok ? 'check' : 'x'} />
                      </span>
                    </div>
                  );
                })}
            </div>
          )}

          <div className={s.formula}>
            <span>Available = in stock − reserved. Saving reserves these boxes at the ship-from site right away.</span>
          </div>
        </aside>

        <div className={s.foot}>
          <button type="button" className="btn primary" disabled={!canSave} onClick={handleSave}>
            <Icon name="check" />
            Save &amp; reserve
          </button>
          <Link to="/office/orders" className="btn ghost">
            Cancel
          </Link>
          {!canOrders && (
            <span className="lead">
              <Icon name="lock" />
              Office only
            </span>
          )}
          {firstShortageLineNo && (
            <span className={s.warnnote}>
              <Icon name="alert" />
              Refused while line {firstShortageLineNo} is short
            </span>
          )}
          {submitError && submitError.code !== 'INSUFFICIENT_STOCK' && <ErrorNote error={submitError} />}
          <span className="grow" />
          <div className={s.keys}>
            <span className="hint">
              <span className="kbd">⌘↵</span>save
            </span>
            <span className="hint">
              <span className="kbd">Tab</span>next field
            </span>
          </div>
        </div>
      </div>
    );
  }

  return body;
}
